import React from "react";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { TProbateLeadForm } from "../../../../../../domain/codecs/form/ProbateLeadForm";
import { Background } from "../../../BuildingBlocks/Background";
import { FrontColors } from "../../../../models/FrontColors";
import { FPLandingTitle } from "../../Simple/FPLandingTitle/FPLandingTitle";
import { FPMarketingParagraphColumn } from "../../Complex/FPMarketingParagraphColumn/FPMarketingParagraphColumn";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { FrontIcon } from "../../../Front/Simple/FrontIcon/FrontIcon";
import { FontRobotoSlab } from "../../../BuildingBlocks/FontRobotoSlab";
import { Text } from "../../../BuildingBlocks/Text";
import { FPMarketingHelplineCTA } from "../../Complex/FPMarketingHelplineCTA/FPMarketingHelplineCTA";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { Shape } from "../../../BuildingBlocks/Shape";
import { FPMarketingParagraph } from "../../Simple/FPMarketingParagraph/FPMarketingParagraph";
import { FPMarketingSectionTitle } from "../../Simple/FPMarketingSectionTitle/FPMarketingSectionTitle";
import { Image } from "../../../BuildingBlocks/Image";
import { FPLandingPageGuidesViewMobile } from "../FPLandingPageGuidesView/FPLandingPageGuidesViewMobile";
import { HorizontalLine } from "../../../BuildingBlocks/HorizontalLine";

type TLandingPageMobileProps = {
    form: TProbateLeadForm;
    onChange: (form: TProbateLeadForm) => void;
    onSubmit: (form: TProbateLeadForm) => void,
};

export const FPLandingPageMobile = (props: React.PropsWithChildren<TLandingPageMobileProps>): JSX.Element => {

    return (
        <SpacingColumn>
            {/* VIEW - HERO PART 1 */}
            <Background
                padding="40px 40px 50px"
                color={FrontColors.PROBATE_0}
            >
                <SpacingColumn spacing="35px">
                    <SpacingColumn spacing="50px">
                        {/* TITLE */}
                        <FPMarketingSectionTitle
                            color={FrontColors.NEUTRAL_18}
                            align="center"
                        >
                            Free Independent Probate Advice
                        </FPMarketingSectionTitle>

                        {/* LILY */}
                        <SpacingRow justifyContent="center">
                            <FrontIcon
                                iconName="lily"
                                size={"70px"}
                                colour={FrontColors.NEUTRAL_18}
                            />
                        </SpacingRow>
                    </SpacingColumn>

                    <FPMarketingParagraphColumn
                        color={FrontColors.NEUTRAL_18}
                        align="center"
                        paragraphs={[
                            <>When someone you love passes away, you need time to <WeightBold>grieve.</WeightBold></>,
                            <>Making sure their <WeightBold>inheritance</WeightBold> goes to the right place can feel <WeightBold>overwhelming.</WeightBold></>,
                            <>You have their <WeightBold>home, money, belongings, pension, tax,</WeightBold> and more to consider.</>,
                            <>This process is what we call <WeightBold>probate.</WeightBold></>
                        ]}
                    />
                </SpacingColumn>
            </Background>
            
            {/* VIEW - HERO PART 2 */}
            <Background
                padding="60px 40px 80px"
                color={FrontColors.NEUTRAL_18}
            >
                <SpacingColumn spacing="50px">
                    {/* SIMPLIFY */}
                    <SpacingColumn spacing="30px">
                        <FontRobotoSlab>
                            <Text
                                width="100%"
                                size={25}
                                lineHeight={1.2}
                                letterSpacing="0.5px"
                                color={FrontColors.PROBATE_0}
                                align="center"
                                weight={500}
                            >
                                Let us simplify it for you.
                            </Text>
                        </FontRobotoSlab>

                        <FPMarketingParagraphColumn
                            align="center"
                            paragraphs={[
                                <>We can give you <WeightBold>advice suited to your situation.</WeightBold></>,
                                <><WeightBold>Avoid</WeightBold> the <WeightBold>confusion</WeightBold>, <WeightBold>mistakes</WeightBold>, and <WeightBold>hours of reading</WeightBold> you would have to do without our help.</>,
                            ]}
                        />
                    </SpacingColumn>
                    
                    {/* WHO ARE WE? */}
                    <SpacingColumn spacing="40px">
                        <SpacingRow justifyContent="center">
                            <FrontIcon
                                iconName="feather"
                                size={"50px"}
                                colour={FrontColors.PROBATE_0}
                            />
                        </SpacingRow>

                        <FPMarketingParagraphColumn
                            align="center"
                            paragraphs={[
                                <><WeightBold>We are Sail Probate.</WeightBold> We give free advice with <WeightBold>no strings attached.</WeightBold></>,
                                <>We do this in the hope that if you like the <WeightBold>high level</WeightBold> of our <WeightBold>free service</WeightBold> you would chose to hire us.</>,
                                <>If not, no hard feelings.</>,
                                <>Enter your <WeightBold>number below</WeightBold> and we will <WeightBold>call you within 30 minutes*.</WeightBold></>
                            ]}
                        />
                    </SpacingColumn>
                    
                    {/* CTA */}
                    <FPMarketingHelplineCTA
                        form={props.form}
                        name="landing-page-0__hero-view"
                        onChange={props.onChange}
                        onSubmit={props.onSubmit}
                        alignDisclaimer="center"
                        disclaimer="*we can only call you during regular working hours, and will never use your number for any other purpose."
                    />
                </SpacingColumn>
            </Background>
            
            {/* VIEW - OUR EXPERTISE */}
            <Background
                padding="60px 40px 70px"
                color={FrontColors.PROBATE_0}
            >
                <SpacingColumn spacing="40px">
                    <SpacingColumn spacing="30px">
                        <FPMarketingSectionTitle color={FrontColors.NEUTRAL_18}>
                            Keri & Jamie’s Expertise.
                        </FPMarketingSectionTitle>

                        <SpacingRow spacing="30px">
                            <Image width="100px" height="100px" borderRadius="100%" url="/static/img/portrait/Keri Expertise 250x250.png" />
                            <Image width="100px" height="100px" borderRadius="100%" url="/static/img/portrait/Jamie Expertise 250x250.png" />
                        </SpacingRow>
                    </SpacingColumn>

                    <FPMarketingParagraphColumn
                        color={FrontColors.NEUTRAL_18}
                        paragraphs={[
                            <>Together we have over <WeightBold>15 years of experience.</WeightBold>  Helping people like you get probate done.</>,
                            <>Replacing <WeightBold>uncertainty</WeightBold> with <WeightBold>confidence</WeightBold> on <WeightBold>over 1000</WeightBold> journeys so far.</>,
                            <>The <WeightBold>biggest</WeightBold> probate service <WeightBold>companies trust us.</WeightBold> Companies like <WeightBold>Zedra,</WeightBold> <WeightBold>Fidelis</WeightBold> and <WeightBold>Prestige</WeightBold> to name a few.</>,
                        ]}
                    />
                </SpacingColumn>
            </Background>

            {/* VIEW - WHY WE DO IT */}
            <Background
                padding="60px 40px 70px"
                color={FrontColors.NEUTRAL_18}
            >
                <SpacingColumn spacing="30px">
                    <FPMarketingSectionTitle align="center">
                        Why We Do It?
                    </FPMarketingSectionTitle>

                    <FPMarketingParagraphColumn
                        align="center"
                        paragraphs={[
                            <>We have gone through probate ourselves and understand how hard it can be during this time.</>,
                            <>Our work has shown us that there is a big lack of free and simple probate advice online.</>,
                            <>We started Sail Probate to fill this knowledge gap. Making a free and accessible helpline available to everyone.  </>,
                        ]}
                    />
                </SpacingColumn>
            </Background>

            {/* VIEW - LET US HELP YOU GET STARTED */}
            <Background
                padding="80px 40px 80px"
                color={FrontColors.PROBATE_0}
            >
                <SpacingColumn spacing="50px">
                    {/* TITLE & PARAGRAPHS */}
                    <SpacingColumn spacing="30px">
                        <FPMarketingSectionTitle color={FrontColors.NEUTRAL_18}>
                            Let us help you get started.
                        </FPMarketingSectionTitle>

                        <FPMarketingParagraphColumn
                            color={FrontColors.NEUTRAL_18}
                            paragraphs={[
                                <> 
                                    <WeightBold>1.</WeightBold> The <WeightBold>first thing</WeightBold> you want to do when going through probate,
                                    is to <WeightBold>list assets.</WeightBold> Examples of assets are: house, items and savings.
                                </>,
                                <>
                                    <WeightBold>2.</WeightBold> The <WeightBold>second thing</WeightBold> you want to do is <WeightBold>list debts.</WeightBold>&nbsp;
                                    Examples of debts are: loans, mortgages and overdrafts.
                                </>,
                                <>
                                    Once you have done this you can either <WeightBold>do the probate yourself</WeightBold> or
                                    hire an <WeightBold>expert.</WeightBold> Your <WeightBold>choice</WeightBold> will affect
                                    how <WeightBold>slow or fast</WeightBold> the process is.
                                </>,
                            ]}
                        />
                    </SpacingColumn>

                    {/* PARAGRAPH & CTA */}
                    <SpacingColumn spacing="20px">
                        <FPMarketingParagraph color={FrontColors.NEUTRAL_18}>
                            If you would like more <WeightBold>help</WeightBold> have us <WeightBold>call you.</WeightBold>
                        </FPMarketingParagraph>

                        <FPMarketingHelplineCTA
                            form={props.form}
                            name="landing-page-0__help-view"
                            coloursInverted={true}
                            onChange={props.onChange}
                            onSubmit={props.onSubmit}
                        />
                    </SpacingColumn>

                    <HorizontalLine color={FrontColors.NEUTRAL_18} />

                    {/* GUIDES */}
                    <FPLandingPageGuidesViewMobile />
                </SpacingColumn>
            </Background>

        </SpacingColumn>
    );
};
